import { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "react-hot-toast";
import { HiCheck, HiX } from "react-icons/hi";
import {
  FiUploadCloud, FiEye, FiEyeOff, FiChevronDown, FiSave, FiClock, FiList, FiPlus,
} from "react-icons/fi";
import { IoArrowBackOutline, IoArrowForwardOutline } from "react-icons/io5";
import { useUploadSingleFileMutation } from "../../../features/admin/imageUpload/imageUploadApi";
import { useAddVendorMutation, useGetAllVendorThemeNamesMutation } from "../../../features/admin/vendor/vendorApi";
import {
  blankConfig, assemblePayload, setOptionUser, pruneUserFromMap, validateStep, validateAll,
  getStepStatus, typesMissing, PROGRAM_CATALOG,
} from "./onboardingConfig";
import {
  useCreateOnboardingMutation, useUpdateOnboardingMutation, useLazyGetOnboardingQuery,
} from "../../../features/admin/onboarding/onboardingApi";

// ─── shared styles ──────────────────────────────────────────────────────────
const inputCls =
  "block w-full px-3 py-2.5 border border-gray-300 rounded-lg bg-white text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500/40 focus:border-blue-500 transition-all";
const selectCls =
  "block w-full pl-3 pr-9 py-2.5 border border-gray-300 rounded-lg bg-white text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500/40 focus:border-blue-500 transition-all appearance-none cursor-pointer";
const labelCls = "block text-sm font-medium text-gray-700 mb-1";
const errCls = "mt-1.5 text-xs text-red-500 flex items-center gap-1";

const STEPS = [
  { label: "Company", desc: "Identity, branding & contact." },
  { label: "User Types", desc: "Who uses the program." },
  { label: "Access & Flows", desc: "Registration, login & approval." },
  { label: "Programs", desc: "Loyalty program enrollment." },
  { label: "Scanning & Points", desc: "QR mode & point logic." },
  { label: "Features", desc: "Toggles & redemption hierarchy." },
  { label: "Review", desc: "Confirm & register." },
];

// ─── small primitives ─────────────────────────────────────────────────────────
const Select = ({ value, onChange, children, className }) => (
  <div className="relative">
    {/* inline styles override global bootstrap `select`/`form-control` rules that clip text */}
    <select
      value={value}
      onChange={onChange}
      className={`${selectCls} ${className || ""}`}
      style={{ height: "auto", lineHeight: "1.5", minHeight: "42px" }}
    >
      {children}
    </select>
    <FiChevronDown className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
  </div>
);

const Field = ({ label, required, error, children, hint }) => (
  <div>
    {label && (
      <label className={labelCls}>
        {label} {required && <span className="text-red-500">*</span>}
      </label>
    )}
    {children}
    {hint && !error && <p className="mt-1 text-xs text-gray-400">{hint}</p>}
    {error && <p className={errCls}><HiX className="h-3.5 w-3.5 shrink-0" /> {error}</p>}
  </div>
);

const Text = ({ value, onChange, ...rest }) => (
  <input className={inputCls} value={value ?? ""} onChange={(e) => onChange(e.target.value)} {...rest} />
);

const SectionCard = ({ title, desc, children }) => (
  <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-6 md:p-8">
    {(title || desc) && (
      <div className="mb-6 pb-4 border-b border-gray-100">
        {title && <h2 className="text-base font-semibold text-gray-900">{title}</h2>}
        {desc && <p className="text-sm text-gray-500 mt-0.5">{desc}</p>}
      </div>
    )}
    {children}
  </div>
);

const Toggle = ({ checked, onChange, label, desc }) => (
  <label className="flex items-start gap-3 p-4 rounded-xl border border-gray-200 hover:border-blue-300 cursor-pointer transition-colors">
    <button
      type="button"
      onClick={() => onChange(!checked)}
      className={`relative mt-0.5 h-5 w-9 shrink-0 rounded-full transition-colors ${checked ? "bg-blue-600" : "bg-gray-300"}`}
    >
      <span className={`absolute top-0.5 h-4 w-4 rounded-full bg-white transition-all ${checked ? "left-[18px]" : "left-0.5"}`} />
    </button>
    <div>
      <p className="text-sm font-medium text-gray-800">{label}</p>
      {desc && <p className="text-xs text-gray-500 mt-0.5">{desc}</p>}
    </div>
  </label>
);

// Vertical status checklist — the primary navigation + progress feedback.
const Checklist = ({ steps, status, current, onJump }) => (
  <nav className="space-y-1">
    {steps.map((s, i) => {
      const st = status[i]?.status || "todo";
      const active = i === current;
      const badge = st === "complete"
        ? "bg-emerald-500 text-white"
        : st === "attention" ? "bg-amber-500 text-white" : "bg-gray-200 text-gray-500";
      const hintColor = st === "complete" ? "text-emerald-600" : st === "attention" ? "text-amber-600" : "text-gray-400";
      return (
        <button key={s.label} type="button" onClick={() => onJump(i)}
          className={`w-full text-left flex items-start gap-3 rounded-xl px-3 py-2.5 transition-colors ${active ? "bg-blue-50 ring-1 ring-blue-200" : "hover:bg-gray-50"}`}>
          <span className={`mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-xs font-semibold ${badge}`}>
            {st === "complete" ? <HiCheck className="h-3.5 w-3.5" /> : st === "attention" ? "!" : i + 1}
          </span>
          <span className="min-w-0 flex-1">
            <span className={`block text-sm font-medium ${active ? "text-blue-700" : "text-gray-800"}`}>{s.label}</span>
            <span className={`block text-xs ${hintColor}`}>{status[i]?.hint}</span>
          </span>
        </button>
      );
    })}
  </nav>
);

const FeedbackBanner = ({ type = "info", title, items }) => {
  const styles = {
    success: "border-emerald-200 bg-emerald-50 text-emerald-700",
    attention: "border-amber-200 bg-amber-50 text-amber-700",
    info: "border-blue-200 bg-blue-50 text-blue-700",
  };
  return (
    <div className={`rounded-xl border px-4 py-3 text-sm ${styles[type]}`}>
      <p className="font-medium flex items-center gap-1.5">
        {type === "success" && <HiCheck className="h-4 w-4 shrink-0" />}{title}
      </p>
      {items?.length > 0 && (
        <ul className="list-disc list-inside mt-1 space-y-0.5 text-xs opacity-90">{items.map((it, i) => <li key={i}>{it}</li>)}</ul>
      )}
    </div>
  );
};

// Per-user-type option matrix. `required` highlights rows with no selection.
const OptionMatrix = ({ userTypes, options, map, onToggle, required }) => {
  if (!userTypes.length) return <p className="text-sm text-gray-400 italic">Add user types first (previous step).</p>;
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm border-separate border-spacing-0">
        <thead>
          <tr>
            <th className="text-left font-medium text-gray-500 pb-3 pr-4">User type</th>
            {options.map((o) => <th key={o} className="text-center font-medium text-gray-500 pb-3 px-3">{o}</th>)}
          </tr>
        </thead>
        <tbody>
          {userTypes.map((ut) => {
            const rowSel = options.some((o) => map[o]?.users?.includes(ut));
            const flag = required && !rowSel;
            return (
              <tr key={ut} className={flag ? "bg-amber-50/50" : ""}>
                <td className="py-2 pr-4 border-t border-gray-100">
                  <span className="font-medium text-gray-800 capitalize">{ut}</span>
                  {flag && <span className="ml-2 text-[10px] font-semibold text-amber-700 bg-amber-100 rounded px-1.5 py-0.5 align-middle">required</span>}
                </td>
                {options.map((o) => {
                  const on = !!map[o]?.users?.includes(ut);
                  return (
                    <td key={o} className="text-center py-2 px-3 border-t border-gray-100">
                      <button type="button" onClick={() => onToggle(o, ut, !on)}
                        className={`h-5 w-5 rounded-md border flex items-center justify-center mx-auto transition-colors ${on ? "bg-blue-600 border-blue-600 text-white" : "border-gray-300 hover:border-blue-400"}`}>
                        {on && <HiCheck className="h-3.5 w-3.5" />}
                      </button>
                    </td>
                  );
                })}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

// ─── password ─────────────────────────────────────────────────────────────────
const PW_RULES = [
  { label: "8+ characters", test: (p) => p.length >= 8 },
  { label: "Uppercase", test: (p) => /[A-Z]/.test(p) },
  { label: "Lowercase", test: (p) => /[a-z]/.test(p) },
  { label: "Number", test: (p) => /[0-9]/.test(p) },
  { label: "Special char", test: (p) => /[^A-Za-z0-9]/.test(p) },
];
const PasswordField = ({ label, value, onChange, showRules, confirm, original, error }) => {
  const [show, setShow] = useState(false);
  const matchOk = confirm && value.length > 0 && value === original;
  return (
    <Field label={label} required error={error}>
      <div className="relative">
        <input className={`${inputCls} pr-10`} type={show ? "text" : "password"} value={value}
          onChange={(e) => onChange(e.target.value)} autoComplete="new-password" placeholder="••••••••" />
        <button type="button" tabIndex={-1} onClick={() => setShow((v) => !v)}
          className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
          {show ? <FiEyeOff size={16} /> : <FiEye size={16} />}
        </button>
      </div>
      {showRules && value && (
        <div className="mt-2 grid grid-cols-2 gap-y-1 gap-x-3">
          {PW_RULES.map((r) => {
            const ok = r.test(value);
            return (
              <div key={r.label} className={`flex items-center gap-1.5 text-xs ${ok ? "text-emerald-600" : "text-gray-400"}`}>
                <span className={`flex h-3.5 w-3.5 items-center justify-center rounded-full border text-[9px] font-bold ${ok ? "border-emerald-500 bg-emerald-500 text-white" : "border-gray-300"}`}>{ok ? "✓" : ""}</span>
                {r.label}
              </div>
            );
          })}
        </div>
      )}
      {confirm && value.length > 0 && (
        <p className={`mt-1.5 text-xs flex items-center gap-1 font-medium ${matchOk ? "text-emerald-600" : "text-red-500"}`}>
          {matchOk ? <HiCheck className="h-3.5 w-3.5" /> : <HiX className="h-3.5 w-3.5" />}
          {matchOk ? "Passwords match" : "Passwords do not match"}
        </p>
      )}
    </Field>
  );
};

const LogoBox = ({ title, url, onPick, uploading }) => (
  <div className="flex flex-col gap-2">
    <p className="text-sm font-medium text-gray-700">{title} <span className="text-red-500">*</span></p>
    <label className={`flex flex-col items-center justify-center gap-1.5 border-2 border-dashed rounded-xl px-4 py-6 cursor-pointer text-center ${url ? "border-blue-300 bg-blue-50/40" : "border-gray-200 hover:border-blue-400"}`}>
      {url ? <img src={url} alt={title} className="max-h-14 object-contain" /> : <FiUploadCloud className="h-6 w-6 text-gray-400" />}
      <span className="text-xs text-gray-500 font-medium">{uploading ? "Uploading…" : url ? "Replace" : "Click to upload"}</span>
      <input type="file" accept="image/*" className="hidden" onChange={(e) => e.target.files?.[0] && onPick(e.target.files[0])} />
    </label>
    {url && <span className="text-xs text-emerald-600 flex items-center gap-1"><HiCheck className="h-3.5 w-3.5" /> Uploaded</span>}
  </div>
);

const ReviewRow = ({ label, value }) =>
  value === null || value === undefined || value === "" ? null : (
    <div className="flex gap-3 py-2 border-b border-gray-100 last:border-0 text-sm">
      <span className="w-40 shrink-0 text-gray-500">{label}</span>
      <span className="text-gray-900 font-medium break-all">{value}</span>
    </div>
  );
const ReviewCard = ({ title, onEdit, children }) => (
  <div>
    <div className="flex items-center justify-between mb-1">
      <p className="text-xs font-semibold uppercase tracking-wider text-gray-400">{title}</p>
      {onEdit && <button type="button" onClick={onEdit} className="text-xs text-blue-600 hover:underline">Edit</button>}
    </div>
    <div className="rounded-xl border border-gray-100 bg-gray-50/50 px-4">{children}</div>
  </div>
);

// helper: summarize an option-map as "option: a, b"
const summarizeMap = (map) =>
  Object.entries(map).filter(([, v]) => v.users?.length).map(([o, v]) => `${o}: ${v.users.join(", ")}`).join(" · ") || "—";

// =============================================================================
const OnboardingBuilder = () => {
  const navigate = useNavigate();
  const [config, setConfig] = useState(blankConfig);
  const [step, setStep] = useState(0);
  const [errors, setErrors] = useState({});

  const [industryData, setIndustryData] = useState([]);
  const [masterTypes, setMasterTypes] = useState([]); // admin app_user_type master list
  const [themeNames, setThemeNames] = useState([]);
  const [uploadingLogo, setUploadingLogo] = useState(null); // 'thumb' | 'nav' | null

  const [searchParams, setSearchParams] = useSearchParams();
  const editId = searchParams.get("id");
  const serverIdRef = useRef(null);   // onboarding_id once saved; ref avoids double-create races during autosave
  const creatingRef = useRef(false);
  const [draftName, setDraftName] = useState("");
  const [savedAt, setSavedAt] = useState(null);

  const [uploadImage] = useUploadSingleFileMutation();
  const [fetchThemeNames, { data: themeNamesData }] = useGetAllVendorThemeNamesMutation();
  const [addVendor, { data: addData, isLoading: adding, error: addErr }] = useAddVendorMutation();
  const [createOnboarding] = useCreateOnboardingMutation();
  const [updateOnboarding] = useUpdateOnboardingMutation();
  const [triggerGetOnboarding] = useLazyGetOnboardingQuery();

  const token = () => sessionStorage.getItem("access_token");

  // ── setters ────────────────────────────────────────────────────────────────
  const patch = (updater) => setConfig((c) => { const n = structuredClone(c); updater(n); return n; });
  const setCompany = (key, val) => patch((n) => { n.company[key] = val; });
  const setSingle = (mapKey, option, ut) => patch((n) => { n[mapKey] = setOptionUser(pruneUserFromMap(n[mapKey], ut), option, ut, true); });
  const toggleMulti = (mapKey, option, ut, on) => patch((n) => { n[mapKey] = setOptionUser(n[mapKey], option, ut, on); });

  // ── effects ──────────────────────────────────────────────────────────────────
  useEffect(() => {
    fetchThemeNames({ token: token() });
    axios.get(`${process.env.REACT_APP_BASE_URL}/admin/industry`, {
      headers: { Authorization: "Bearer " + token() },
    }).then((r) => setIndustryData(r.data.body || [])).catch(console.error);
    axios.get(`${process.env.REACT_APP_BASE_URL}/admin/appUserType`, {
      headers: { Authorization: "Bearer " + token() },
    }).then((r) => setMasterTypes(r.data.body || [])).catch(console.error);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (themeNamesData?.body) setThemeNames(themeNamesData.body.map((t) => t.tenant_name));
  }, [themeNamesData]);

  // resume an existing onboarding from ?id=
  useEffect(() => {
    if (!editId) return;
    triggerGetOnboarding({ token: token(), id: editId }).unwrap()
      .then((res) => {
        const row = res.body;
        if (!row || row === "Not Found") { toast.error("Onboarding not found"); return; }
        if (row.status === "completed") { toast("This onboarding is already completed", { icon: "ℹ️" }); }
        setConfig({ ...blankConfig(), ...(row.config || {}) });
        setStep(row.current_step || 0);
        serverIdRef.current = row.onboarding_id;
        setDraftName(row.name || "");
      })
      .catch(() => toast.error("Could not load onboarding"));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editId]);

  // auto theme_name from company name
  useEffect(() => {
    setCompany("theme_name", config.company.name.replaceAll(" ", "-").toLowerCase());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [config.company.name]);

  useEffect(() => {
    if (addData) {
      toast.dismiss();
      toast.success("Client registered successfully!");
      // mark the onboarding record completed + link the created tenant/company
      if (serverIdRef.current) {
        updateOnboarding({
          token: token(),
          id: serverIdRef.current,
          body: {
            status: "completed",
            tenant_id: addData.body?.tenant?.tenant_id ?? null,
            company_id: addData.body?.companyDetail?.company_id ?? null,
            current_step: STEPS.length - 1,
          },
        }).catch(() => {});
      }
      navigate("/");
    }
    if (addErr) { toast.dismiss(); toast.error(addErr?.data?.message || "Error registering client"); }
    if (adding) toast.loading("Registering client…");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [addData, addErr, adding]);

  // ── server-backed drafts ───────────────────────────────────────────────────────
  // hasProgress → anything worth persisting (so incomplete onboardings save & resume).
  const hasProgress = () => {
    const c = config.company;
    return !!(c.name || c.email || c.mobile || c.gstin || c.industry_id || config.user_types.length || serverIdRef.current);
  };
  const lastSaveErrorRef = useRef(null);
  const persistDraft = async (curStep = step, { force = false } = {}) => {
    if (!force && !hasProgress()) return false;
    // name always tracks the company name so it updates on every save/update
    const name = config.company.name?.trim() || draftName || "Untitled onboarding";
    if (name !== draftName) setDraftName(name);
    const body = { name, config, step_status: getStepStatus(config), current_step: curStep };
    try {
      if (serverIdRef.current) {
        await updateOnboarding({ token: token(), id: serverIdRef.current, body }).unwrap();
      } else {
        if (creatingRef.current) return false;      // guard concurrent autosave creates
        creatingRef.current = true;
        const res = await createOnboarding({ token: token(), body }).unwrap();
        const newId = res.body.onboarding_id;
        serverIdRef.current = newId;
        setSearchParams({ id: String(newId) }, { replace: true }); // survive refresh
        creatingRef.current = false;
      }
      setSavedAt(Date.now());
      lastSaveErrorRef.current = null;
      return true;
    } catch (e) {
      creatingRef.current = false;
      lastSaveErrorRef.current =
        e?.data?.message || (e?.status ? `Request failed (${e.status})` : "Network error");
      console.error("Failed to save onboarding", e);
      return false;
    }
  };
  const saveDraftManual = async () => {
    const ok = await persistDraft(step, { force: true });
    if (ok) toast.success("Draft saved — resume anytime from All onboardings", { id: "draftSaved" });
    else toast.error(`Couldn't save draft: ${lastSaveErrorRef.current || "unknown error"}`, { id: "draftSaved" });
  };

  // autosave (debounced) whenever config changes and there's meaningful content
  const saveTimer = useRef(null);
  useEffect(() => {
    if (!hasProgress()) return;
    clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(() => persistDraft(), 1200);
    return () => clearTimeout(saveTimer.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [config]);

  // ── logo upload ────────────────────────────────────────────────────────────────
  const uploadLogo = async (file, which) => {
    setUploadingLogo(which);
    try {
      const fd = new FormData();
      fd.append("image", file);
      const res = await uploadImage({ body: fd, token: token() }).unwrap();
      setCompany(which === "thumb" ? "thumbnailLogo" : "navbarLogo", res.body.fileLink);
    } catch {
      toast.error("Image upload failed");
    } finally {
      setUploadingLogo(null);
    }
  };

  // ── industry select → seed user_types ─────────────────────────────────────────
  const onSelectIndustry = (id) => {
    const ind = industryData.find((d) => String(d.industry_id) === String(id));
    patch((n) => {
      n.company.industry_id = Number(id);
      n.company.industry_name = ind?.name || "";
      if (ind?.users?.length && !n.user_types.length) n.user_types = [...ind.users];
    });
  };

  // ── user type add/remove ──────────────────────────────────────────────────────
  const [newType, setNewType] = useState("");
  const addUserType = () => {
    const t = newType.trim().toLowerCase();
    if (!t || config.user_types.includes(t)) return;
    patch((n) => { n.user_types.push(t); });
    setNewType("");
  };
  const addExistingType = (t) => {
    if (!t || config.user_types.includes(t)) return;
    patch((n) => { n.user_types.push(t); });
  };
  const removeUserType = (t) => patch((n) => {
    n.user_types = n.user_types.filter((x) => x !== t);
    ["registration_options", "login_options", "approval_flow_options", "product_availing"].forEach((k) => {
      n[k] = pruneUserFromMap(n[k], t);
    });
    delete n.redemption_hierarchy.gift_mapped_users[t];
  });

  // ── navigation ────────────────────────────────────────────────────────────────
  const goStep = (i) => { persistDraft(i); setErrors({}); setStep(i); };
  const next = () => { const e = validateStep(config, step); setErrors(e); goStep(Math.min(step + 1, STEPS.length - 1)); };
  const back = () => goStep(Math.max(step - 1, 0));

  const finalErrors = useMemo(() => validateAll(config), [config]);
  const stepStatus = useMemo(() => getStepStatus(config), [config]);
  const completeCount = stepStatus.filter((s) => s.status === "complete").length;

  const handleFinish = () => {
    if (finalErrors.length) { toast.error("Fix the highlighted items before registering"); setStep(0); return; }
    addVendor({ token: token(), body: assemblePayload(config) });
  };

  const ut = config.user_types;
  const savedLabel = savedAt ? `Saved ${new Date(savedAt).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}` : null;

  // ── steps ──────────────────────────────────────────────────────────────────────
  const renderStep = () => {
    switch (step) {
      case 0: return (
        <SectionCard title="Company basics" desc="Identity, credentials, branding and contact details.">
          <div className="grid md:grid-cols-2 gap-5">
            <Field label="Company name" required error={errors.name}><Text value={config.company.name} onChange={(v) => setCompany("name", v)} placeholder="Acme Pvt Ltd" /></Field>
            <Field label="Domain (theme name)" hint={themeNames.includes(config.company.theme_name) ? "" : "Auto-generated from name"} error={themeNames.includes(config.company.theme_name) ? `"${config.company.theme_name}" is already taken` : null}>
              <Text value={config.company.theme_name} onChange={(v) => setCompany("theme_name", v)} />
            </Field>
            <Field label="Email" required error={errors.email}><Text type="email" value={config.company.email} onChange={(v) => setCompany("email", v)} placeholder="admin@acme.com" /></Field>
            <Field label="Mobile" required error={errors.mobile}><Text value={config.company.mobile} onChange={(v) => setCompany("mobile", v.replace(/\D/g, ""))} placeholder="10-digit" maxLength={10} inputMode="numeric" /></Field>
            <PasswordField label="Password" value={config.company.password} onChange={(v) => setCompany("password", v)} showRules error={errors.password} />
            <PasswordField label="Confirm password" value={config.company.rePassword} onChange={(v) => setCompany("rePassword", v)} confirm original={config.company.password} error={errors.rePassword} />
            <Field label="GSTIN" required error={errors.gstin}><Text value={config.company.gstin} onChange={(v) => setCompany("gstin", v.toUpperCase().slice(0, 15))} placeholder="15-char GSTIN" maxLength={15} /></Field>
            <Field label="Website"><Text value={config.company.website} onChange={(v) => setCompany("website", v)} placeholder="https://" /></Field>
            <Field label="Industry" required error={errors.industry_id}>
              <Select value={config.company.industry_id} onChange={(e) => onSelectIndustry(e.target.value)}>
                <option value={0}>Select industry…</option>
                {industryData.map((d) => <option key={d.industry_id} value={d.industry_id}>{d.name}</option>)}
              </Select>
            </Field>
            <Field label="Contact person"><Text value={config.company.contact_person_name} onChange={(v) => setCompany("contact_person_name", v)} /></Field>
            <Field label="Contact number"><Text value={config.company.contact_person_number} onChange={(v) => setCompany("contact_person_number", v)} /></Field>
            <Field label="Support email"><Text value={config.company.customer_support_email} onChange={(v) => setCompany("customer_support_email", v)} /></Field>
            <Field label="Support mobile"><Text value={config.company.customer_support_mobile} onChange={(v) => setCompany("customer_support_mobile", v)} /></Field>
          </div>

          <div className="mt-6 pt-5 border-t border-gray-100 grid md:grid-cols-2 gap-6">
            <LogoBox title="Login page logo" url={config.company.thumbnailLogo} uploading={uploadingLogo === "thumb"} onPick={(f) => uploadLogo(f, "thumb")} />
            <LogoBox title="Navbar logo" url={config.company.navbarLogo} uploading={uploadingLogo === "nav"} onPick={(f) => uploadLogo(f, "nav")} />
          </div>
          {errors.logo && <p className={errCls}><HiX className="h-3.5 w-3.5" /> {errors.logo}</p>}
        </SectionCard>
      );

      case 1: return (
        <SectionCard title="App user types" desc="Foundational — every flow below is configured per user type. Seeded from the selected industry; add existing types or create new ones.">
          {/* Existing/master user types */}
          <div>
            <p className={labelCls}>Choose from existing user types</p>
            {masterTypes.length === 0 ? (
              <p className="text-sm text-gray-400 italic">No master user types found.</p>
            ) : (
              <div className="flex flex-wrap gap-2">
                {masterTypes.map((m) => {
                  const val = m.user_type;
                  const on = ut.includes(val);
                  return (
                    <button key={m.user_type_id || val} type="button"
                      onClick={() => (on ? removeUserType(val) : addExistingType(val))}
                      className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium capitalize border transition-colors ${on ? "bg-blue-600 border-blue-600 text-white" : "border-gray-300 text-gray-600 hover:border-blue-400"}`}>
                      {on ? <HiCheck className="h-3.5 w-3.5" /> : <FiPlus className="h-3.5 w-3.5" />}
                      {m.name || val}
                    </button>
                  );
                })}
              </div>
            )}
          </div>

          {/* Custom new type */}
          <div className="mt-6">
            <p className={labelCls}>Or add a new user type</p>
            <div className="flex gap-2 max-w-md">
              <input className={inputCls} value={newType} placeholder="e.g. plumber"
                onChange={(e) => setNewType(e.target.value)} onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addUserType())} />
              <button type="button" onClick={addUserType} className="inline-flex items-center gap-1.5 px-4 py-2.5 rounded-lg bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium">
                <FiPlus className="h-4 w-4" /> Add
              </button>
            </div>
          </div>

          {errors.user_types && <p className={errCls}><HiX className="h-3.5 w-3.5" /> {errors.user_types}</p>}

          {/* Selected */}
          <div className="mt-6 pt-5 border-t border-gray-100">
            <p className={labelCls}>Selected user types {ut.length > 0 && <span className="text-gray-400 font-normal">({ut.length})</span>}</p>
            <div className="flex flex-wrap gap-2">
              {ut.map((t) => (
                <span key={t} className="inline-flex items-center gap-2 pl-3 pr-2 py-1.5 rounded-full bg-blue-50 text-blue-700 text-sm font-medium capitalize">
                  {t}
                  <button type="button" onClick={() => removeUserType(t)} className="text-blue-400 hover:text-red-500"><HiX className="h-4 w-4" /></button>
                </span>
              ))}
              {!ut.length && <p className="text-sm text-gray-400 italic">None selected yet. Selecting an industry seeds them automatically.</p>}
            </div>
          </div>
        </SectionCard>
      );

      case 2: {
        const loginMiss = typesMissing(config, "login_options");
        const apprMiss = typesMissing(config, "approval_flow_options");
        const missing = [
          ...(loginMiss.length ? [`Login method for: ${loginMiss.join(", ")}`] : []),
          ...(apprMiss.length ? [`Approval flow for: ${apprMiss.join(", ")}`] : []),
        ];
        return (
          <div className="space-y-5">
            {!ut.length ? (
              <FeedbackBanner type="info" title="Add user types first — this step configures each one." />
            ) : missing.length ? (
              <FeedbackBanner type="attention" title="Every user type needs a login method and approval flow:" items={missing} />
            ) : (
              <FeedbackBanner type="success" title="Login and approval set for all user types." />
            )}
            <SectionCard title="Registration" desc="Which user types can self-register (optional).">
              <OptionMatrix userTypes={ut} options={["Registration"]} map={config.registration_options}
                onToggle={(o, u, on) => toggleMulti("registration_options", o, u, on)} />
            </SectionCard>
            <SectionCard title="Login method" desc="Each user type logs in via OTP or Password (UID). Pick one — required.">
              <OptionMatrix required userTypes={ut} options={["Otp", "Password"]} map={config.login_options}
                onToggle={(o, u) => setSingle("login_options", o, u)} />
            </SectionCard>
            <SectionCard title="Approval flow" desc="How new accounts of each type are approved — required.">
              <OptionMatrix required userTypes={ut} options={["Manual", "AutoApproval"]} map={config.approval_flow_options}
                onToggle={(o, u) => setSingle("approval_flow_options", o, u)} />
            </SectionCard>
          </div>
        );
      }

      case 3: {
        const notEnrolled = typesMissing(config, "product_availing");
        return (
          <div className="space-y-5">
            {!ut.length ? (
              <FeedbackBanner type="info" title="Add user types first." />
            ) : notEnrolled.length ? (
              <FeedbackBanner type="attention" title="Not yet enrolled in any program:" items={[notEnrolled.join(", ")]} />
            ) : (
              <FeedbackBanner type="success" title="Every user type is enrolled in a program." />
            )}
            <SectionCard title="Program selection & enrollment" desc="Enroll each user type into one or more loyalty programs.">
              <OptionMatrix userTypes={ut} options={PROGRAM_CATALOG} map={config.product_availing}
                onToggle={(o, u, on) => toggleMulti("product_availing", o, u, on)} />
            </SectionCard>
          </div>
        );
      }

      case 4: return (
        <div className="space-y-5">
          <SectionCard title="Scanning setup" desc="MAIN = single-wallet (stage 1). SCHEME = client builds schemes in-tenant (stage 2).">
            <div className="grid md:grid-cols-3 gap-5">
              <Field label="Scanning mode" required>
                <Select value={config.scanning.scanning_type} onChange={(e) => patch((n) => { n.scanning.scanning_type = e.target.value; })}>
                  <option value="main">MAIN — single wallet</option>
                  <option value="scheme">SCHEME — per-scheme logic</option>
                </Select>
              </Field>
              <Field label="QR type"><Select value={config.scanning.qr_type} onChange={(e) => patch((n) => { n.scanning.qr_type = e.target.value; })}>
                <option value="0">0</option><option value="1">1</option>
              </Select></Field>
              <Field label="QR prefix"><Text value={config.scanning.qr_prefix} onChange={(v) => patch((n) => { n.scanning.qr_prefix = v; })} /></Field>
            </div>
            {config.scanning.scanning_type === "scheme" && (
              <p className="mt-4 text-xs text-amber-600 bg-amber-50 rounded-lg px-3 py-2">Point config below acts as a fallback; schemes are configured by the client inside the tenant.</p>
            )}
          </SectionCard>

          <SectionCard title="Loyalty rules" desc="Rule engine applied to scans.">
            <Toggle checked={config.features.loyalty_rules} label="Enable loyalty rules" desc="Run the loyalty rule engine on qualifying scans"
              onChange={(v) => patch((n) => { n.features.loyalty_rules = v; })} />
            {config.features.loyalty_rules && (
              <div className="mt-3 rounded-lg bg-blue-50 border border-blue-100 px-4 py-3 text-sm text-blue-700">
                Loyalty rules type: <span className="font-semibold uppercase">{config.scanning.scanning_type}</span> 
              </div>
            )}
          </SectionCard>

          <SectionCard title="Basic point configuration" desc="How scan points are computed.">
            <div className="grid md:grid-cols-2 gap-4">
              <Toggle checked={config.points_sharing.flat_points} label="Flat points" desc="Award a fixed number of points"
                onChange={(v) => patch((n) => { n.points_sharing.flat_points = v; if (v) n.points_sharing.percentage_points = false; })} />
              <Toggle checked={config.points_sharing.percentage_points} label="Percentage points" desc="Award a % of value"
                onChange={(v) => patch((n) => { n.points_sharing.percentage_points = v; if (v) n.points_sharing.flat_points = false; })} />
            </div>
            {config.points_sharing.percentage_points && (
              <div className="grid md:grid-cols-2 gap-4 mt-4">
                <Field label="Percentage value"><Text value={config.points_sharing.percentage_points_value} onChange={(v) => patch((n) => { n.points_sharing.percentage_points_value = v; })} placeholder="e.g. 100" /></Field>
                <Field label="Percentage on"><Select value={config.points_sharing.percentage_on || ""} onChange={(e) => patch((n) => { n.points_sharing.percentage_on = e.target.value; })}>
                  <option value="">Select…</option><option value="qr_mrp">QR MRP</option><option value="product_mrp">Product MRP</option>
                </Select></Field>
              </div>
            )}
            <div className="mt-4">
              <Toggle checked={config.points_sharing.is_point_reserved} label="Reserve a portion of points" desc="Hold back a % until conditions are met"
                onChange={(v) => patch((n) => { n.points_sharing.is_point_reserved = v; })} />
              {config.points_sharing.is_point_reserved && (
                <div className="mt-3 max-w-xs"><Field label="Reserved %"><Text value={config.points_sharing.point_reserved_percentage} onChange={(v) => patch((n) => { n.points_sharing.point_reserved_percentage = v; })} placeholder="e.g. 10" /></Field></div>
              )}
            </div>
          </SectionCard>
        </div>
      );

      case 5: return (
        <div className="space-y-5">
          <SectionCard title="Features & toggles">
            <div className="grid md:grid-cols-2 gap-4">
              <Toggle checked={config.features.multi_pop_on_qr} label="Multi-pop on QR" desc="Allow multiple pops per QR scan"
                onChange={(v) => patch((n) => { n.features.multi_pop_on_qr = v; })} />
              <Toggle checked={config.features.dream_gift} label="Dream gift" desc="Enable dream-gift goal saving"
                onChange={(v) => patch((n) => { n.features.dream_gift = v; })} />
              <Toggle checked={config.features.registration_bonus} label="Registration bonus" desc="Award points on signup (amount set per type in-tenant)"
                onChange={(v) => patch((n) => { n.features.registration_bonus = v; })} />
              <Toggle checked={config.scanning.scanning_type === "scheme"} label="Scheme mode" desc="Mirrors Scanning mode (set in previous step)"
                onChange={(v) => patch((n) => { n.scanning.scanning_type = v ? "scheme" : "main"; })} />
            </div>
          </SectionCard>

          <SectionCard title="Redemption hierarchy" desc="Map which user types can redeem gifts on behalf of others.">
            <Toggle checked={config.redemption_hierarchy.is_mapped_users} label="Enable mapped-user redemption"
              onChange={(v) => patch((n) => { n.redemption_hierarchy.is_mapped_users = v; })} />
            {config.redemption_hierarchy.is_mapped_users && (
              <div className="mt-4 space-y-3">
                {ut.map((owner) => (
                  <div key={owner} className="flex flex-wrap items-center gap-2">
                    <span className="w-28 text-sm font-medium text-gray-700 capitalize">{owner} →</span>
                    {ut.filter((x) => x !== owner).map((target) => {
                      const on = (config.redemption_hierarchy.gift_mapped_users[owner] || []).includes(target);
                      return (
                        <button key={target} type="button"
                          onClick={() => patch((n) => {
                            const cur = new Set(n.redemption_hierarchy.gift_mapped_users[owner] || []);
                            on ? cur.delete(target) : cur.add(target);
                            n.redemption_hierarchy.gift_mapped_users[owner] = [...cur];
                          })}
                          className={`px-3 py-1.5 rounded-full text-xs font-medium capitalize border ${on ? "bg-blue-600 border-blue-600 text-white" : "border-gray-300 text-gray-600 hover:border-blue-400"}`}>
                          {target}
                        </button>
                      );
                    })}
                  </div>
                ))}
              </div>
            )}
          </SectionCard>
        </div>
      );

      case 6: return (
        <div className="space-y-5">
          {finalErrors.length > 0 && (
            <div className="rounded-xl border border-red-200 bg-red-50 p-4">
              <p className="text-sm font-semibold text-red-700 mb-1">Resolve before registering:</p>
              <ul className="list-disc list-inside text-sm text-red-600 space-y-0.5">{finalErrors.map((m, i) => <li key={i}>{m}</li>)}</ul>
            </div>
          )}
          <SectionCard title="Review configuration" desc="Confirm everything before the tenant is provisioned.">
            <div className="space-y-5">
              <ReviewCard title="Company" onEdit={() => goStep(0)}>
                <ReviewRow label="Name" value={config.company.name} />
                <ReviewRow label="Email" value={config.company.email} />
                <ReviewRow label="Mobile" value={config.company.mobile} />
                <ReviewRow label="GSTIN" value={config.company.gstin} />
                <ReviewRow label="Industry" value={config.company.industry_name} />
              </ReviewCard>
              <ReviewCard title="User types" onEdit={() => goStep(1)}><ReviewRow label="Types" value={ut.join(", ")} /></ReviewCard>
              <ReviewCard title="Access & flows" onEdit={() => goStep(2)}>
                <ReviewRow label="Registration" value={summarizeMap(config.registration_options)} />
                <ReviewRow label="Login" value={summarizeMap(config.login_options)} />
                <ReviewRow label="Approval" value={summarizeMap(config.approval_flow_options)} />
              </ReviewCard>
              <ReviewCard title="Programs" onEdit={() => goStep(3)}><ReviewRow label="Enrollment" value={summarizeMap(config.product_availing)} /></ReviewCard>
              <ReviewCard title="Scanning & points" onEdit={() => goStep(4)}>
                <ReviewRow label="Mode" value={config.scanning.scanning_type} />
                <ReviewRow label="Points" value={config.points_sharing.flat_points ? "Flat" : config.points_sharing.percentage_points ? `Percentage (${config.points_sharing.percentage_points_value || "?"}% on ${config.points_sharing.percentage_on || "?"})` : "—"} />
              </ReviewCard>
              <ReviewCard title="Features" onEdit={() => goStep(5)}>
                <ReviewRow label="Enabled" value={Object.entries(config.features).filter(([, v]) => v).map(([k]) => k).join(", ") || "—"} />
                <ReviewRow label="Redemption map" value={config.redemption_hierarchy.is_mapped_users ? "Enabled" : "Off"} />
              </ReviewCard>
            </div>
          </SectionCard>
        </div>
      );

      default: return null;
    }
  };

  return (
    <div className="page-content bg-gray-50 min-h-screen">
      <div className="w-full px-4 md:px-8 py-6">
        {/* header */}
        <div className="flex items-start justify-between gap-4 mb-5 flex-wrap">
          <div>
            <h1 className="text-xl font-bold text-gray-900">Client Onboarding</h1>
            <p className="text-sm text-gray-500 mt-0.5">Configure and register a new client end-to-end.</p>
          </div>
          <div className="flex items-center gap-2">
            {savedLabel && <span className="text-xs text-gray-400 flex items-center gap-1"><FiClock className="h-3.5 w-3.5" /> {savedLabel}</span>}
            <button type="button" onClick={saveDraftManual} className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border border-gray-300 bg-white text-sm font-medium text-gray-700 hover:bg-gray-50">
              <FiSave className="h-4 w-4" /> Save draft
            </button>
            <button type="button" onClick={() => navigate("/onboardings")} className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border border-gray-300 bg-white text-sm font-medium text-gray-700 hover:bg-gray-50">
              <FiList className="h-4 w-4" /> All onboardings
            </button>
            <button type="button" onClick={() => navigate("/onboardings")} className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium text-gray-500 hover:text-gray-700">
              <HiX className="h-4 w-4" /> Cancel
            </button>
          </div>
        </div>

        {/* builder: sidebar checklist + main */}
        <div className="flex flex-col lg:flex-row gap-6">
          <aside className="lg:w-72 shrink-0">
            <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-3 lg:sticky lg:top-4">
              <div className="px-3 pt-2 pb-3">
                <div className="flex items-center justify-between text-xs text-gray-500 mb-1.5">
                  <span className="font-medium">Progress</span>
                  <span>Step {step + 1} of {STEPS.length} · {completeCount} done</span>
                </div>
                <div className="h-1.5 rounded-full bg-gray-100 overflow-hidden">
                  <div className="h-full bg-blue-600 transition-all duration-300" style={{ width: `${((step + 1) / STEPS.length) * 100}%` }} />
                </div>
              </div>
              <Checklist steps={STEPS} status={stepStatus} current={step} onJump={goStep} />
            </div>
          </aside>

          <main className="flex-1 min-w-0">
            <div className="mb-4">
              <div className="flex items-center gap-2">
                <span className="text-xs font-semibold text-blue-600 bg-blue-50 rounded-full px-2 py-0.5">Step {step + 1} of {STEPS.length}</span>
                <h2 className="text-lg font-semibold text-gray-900">{STEPS[step].label}</h2>
              </div>
              <p className="text-sm text-gray-500 mt-0.5">{STEPS[step].desc}</p>
            </div>

            {renderStep()}

            {/* nav */}
            <div className="flex items-center justify-between mt-6">
              <button type="button" onClick={back} disabled={step === 0}
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg border border-gray-300 bg-white text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed">
                <IoArrowBackOutline className="h-4 w-4" /> Back
              </button>
              {step < STEPS.length - 1 ? (
                <button type="button" onClick={next} className="inline-flex items-center gap-2 px-6 py-2.5 rounded-lg bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold shadow-sm">
                  Continue <IoArrowForwardOutline className="h-4 w-4" />
                </button>
              ) : (
                <button type="button" onClick={handleFinish} disabled={adding || finalErrors.length > 0}
                  className="inline-flex items-center gap-2 px-6 py-2.5 rounded-lg bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold shadow-sm disabled:opacity-50 disabled:cursor-not-allowed">
                  {adding ? <><span className="h-4 w-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> Registering…</> : <><HiCheck className="h-4 w-4" /> Register Client</>}
                </button>
              )}
            </div>
          </main>
        </div>
      </div>
    </div>
  );
};

export default OnboardingBuilder;
